import {
  MemberClockStateType,
  MemberPermissionFilter,
  MemberSort,
  PositionFilter,
  QueryMemberPickQuery,
  QueryMemberPickQueryVariables,
} from '__generated__/graphql';
import { ArchivedStatus } from 'components/domain/archived/ArchivedPicker/ArchivedPicker';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import { getApolloMemberNameSearch } from 'hooks/models/member/useApolloMemberNameSearch';
import { getApolloMemberNameSort } from 'hooks/models/member/useApolloMemberNameSort';
import useEmployeeNameFormat from 'hooks/settings/local-settings/useEmployeeNameFormat';
import { useCallback } from 'react';
import { NonNullArrayField } from 'types/util/NonNullArrayField';
import { getApolloArchivedTimestampComparison } from 'utils/archivedUtils';
import { membersQuery } from '../employee-picker-queries';

export type EmployeePickerMember = NonNullArrayField<QueryMemberPickQuery['members']>;

export interface UseEmployeePickerDataOptions {
  blacklistedIds?: string[];
  includedIds?: string[];
  permissions?: MemberPermissionFilter;
  positionFilter?: PositionFilter;
  archivedStatus?: ArchivedStatus;
  includeClockedIn?: boolean;
}

export function useEmployeePickerData({
  blacklistedIds,
  includedIds,
  permissions,
  positionFilter,
  archivedStatus,
  includeClockedIn,
}: UseEmployeePickerDataOptions = {}) {
  const [employeeNameFormat] = useEmployeeNameFormat();
  const graphqlClient = useGraphQLClient();

  const fetchPage = useCallback(
    async (after: string | null, first: number, searchDebounce: string): Promise<EmployeePickerMember[]> => {
      const result = await graphqlClient.request<QueryMemberPickQuery, QueryMemberPickQueryVariables>({
        document: membersQuery,
        variables: {
          after,
          first,
          filter: {
            archivedOn: getApolloArchivedTimestampComparison(archivedStatus ?? 'unarchived'),
            search: searchDebounce
              ? getApolloMemberNameSearch(employeeNameFormat ?? 'FIRST_LAST', searchDebounce)
              : undefined,
            id: blacklistedIds
              ? { doesNotContain: blacklistedIds }
              : includedIds
                ? { contains: includedIds }
                : undefined,
            permissions,
            clockState: includeClockedIn === false ? { state: MemberClockStateType.ClockedOut } : undefined,
            position: positionFilter,
          },
          sort: getApolloMemberNameSort(employeeNameFormat ?? 'FIRST_LAST', 'asc') as MemberSort,
        },
      });

      return result.members ?? [];
    },
    [
      graphqlClient,
      employeeNameFormat,
      archivedStatus,
      blacklistedIds,
      includedIds,
      permissions,
      includeClockedIn,
      positionFilter,
    ]
  );

  const fetchSelected = useCallback(
    async (ids: string[]): Promise<EmployeePickerMember[]> => {
      const result = await graphqlClient.request<QueryMemberPickQuery, QueryMemberPickQueryVariables>({
        document: membersQuery,
        variables: { filter: { id: { contains: ids } } },
      });

      return result.members ?? [];
    },
    [graphqlClient]
  );

  return { fetchPage, fetchSelected, employeeNameFormat };
}
